import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Spin, Alert, Space, Card, Slider, Typography, Row, Col, Tooltip } from 'antd';
import {
  DownloadOutlined,
  LoadingOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  SoundOutlined,
  CustomerServiceOutlined
} from '@ant-design/icons';
import { useSheshaApplication } from '@shesha-io/reactjs';
import axios, { AxiosError } from 'axios';
import './styles.css';

const { Text } = Typography;

// Type guard for Axios errors
const isAxiosError = (error: unknown): error is AxiosError => {
  return axios.isAxiosError(error);
};

export interface ICallRecordingPlayerProps {
  label?: string;
  url?: string;
  recordingId?: string;
  recordingDate?: string;
  showDownloadButton?: boolean;
  showControls?: boolean;
  compact?: boolean; // Compact mode: single line with minimal controls
  autoplay?: boolean;
  loop?: boolean;
  preload?: 'none' | 'metadata' | 'auto';
  downloadButtonText?: string;
  loadingText?: string;
  noRecordingText?: string;
  // Style properties
  backgroundColor?: string;
  borderColor?: string;
  borderRadius?: number;
  borderWidth?: number;
  padding?: number;
  width?: string;
  height?: string;
}

const CallRecordingPlayer: React.FC<ICallRecordingPlayerProps> = ({
  label = 'Call Recording',
  url = '/api/services/Telephony/AWS/CallDetails/DownloadRecording?id=',
  recordingId,
  recordingDate,
  showDownloadButton = true,
  showControls = true,
  compact = false,
  autoplay = false,
  loop = false,
  preload = 'metadata',
  downloadButtonText = 'Download Recording',
  loadingText = 'Loading recording...',
  noRecordingText = 'Please provide a recording ID to play or download the recording.',
  // Style properties
  backgroundColor,
  borderColor,
  borderRadius,
  borderWidth,
  padding,
  width,
  height,
}) => {
  const { backendUrl, httpHeaders } = useSheshaApplication();
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<boolean>(false);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const [volume, setVolume] = useState<number>(1);
  const audioRef = useRef<HTMLAudioElement>(null);
  const audioUrlRef = useRef<string | null>(null);

  // Build custom card style from props
  const cardStyle: React.CSSProperties = {
    width: width || '100%',
    height: height,
    backgroundColor: backgroundColor,
    borderColor: borderColor,
    borderRadius: borderRadius ? `${borderRadius}px` : undefined,
    borderWidth: borderWidth ? `${borderWidth}px` : undefined,
    padding: padding ? `${padding}px` : undefined,
  };

  useEffect(() => {
    // Cleanup function to revoke object URL when component unmounts
    return () => {
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
      }
    };
  }, []);

  const loadRecording = useCallback(async () => {
    if (!recordingId) {
      setError('No recording ID provided');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Revoke previous URL before creating new one to prevent memory leaks
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
        audioUrlRef.current = null;
      }

      // URL-encode the recording ID to handle special characters like colons
      const encodedRecordingId = encodeURIComponent(recordingId);

      // Construct the full URL with backend URL
      const fullUrl = `${backendUrl}${url}${encodedRecordingId}`;

      // Fetch the recording with authentication from Shesha context
      const response = await axios.get(fullUrl, {
        responseType: 'blob',
        withCredentials: true,
        headers: httpHeaders, // Use Shesha's authenticated headers
      });

      // Get content type from response, default to audio/mpeg.
      // axios types a header as string | number | boolean | string[] | AxiosHeaders, and
      // BlobPropertyBag.type only accepts a string, so anything else falls back to the default.
      const rawContentType = response.headers['content-type'];
      const contentType = typeof rawContentType === 'string' ? rawContentType : 'audio/mpeg';

      // Create object URL for the blob
      const blob = new Blob([response.data], { type: contentType });
      const objectUrl = URL.createObjectURL(blob);

      // Store in ref and state
      audioUrlRef.current = objectUrl;
      setAudioUrl(objectUrl);
      setLoading(false);
    } catch (err: unknown) {
      console.error('Error loading recording:', err);

      if (isAxiosError(err)) {
        setError(
          err.response?.status === 404
            ? 'Recording not found'
            : err.response?.status === 401
            ? 'Unauthorized: Please log in'
            : 'Failed to load recording. Please try again.'
        );
      } else {
        setError('Failed to load recording. Please try again.');
      }
      setLoading(false);
    }
  }, [recordingId, url, backendUrl, httpHeaders]);

  useEffect(() => {
    if (recordingId && audioRef.current) {
      loadRecording();
    }
  }, [recordingId, loadRecording]);

  // Ensure audio element loads when URL changes
  useEffect(() => {
    if (audioUrl && audioRef.current) {
      audioRef.current.load();
    }
  }, [audioUrl]);

  // Audio event listeners
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleDurationChange = () => setDuration(audio.duration);
    const handleEnded = () => setIsPlaying(false);
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);

    return () => {
      // Cleanup function - audio ref is captured in closure so it's safe to use
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
    };
  }, [audioUrl]);

  const formatTime = (seconds: number): string => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const extractDateFromRecordingId = (id?: string): string | undefined => {
    if (!id) return undefined;

    // Check if the ID contains an underscore followed by a date
    // Format: uuid_2025-08-21T11:07:02.327Z
    const parts = id.split('_');
    if (parts.length === 2) {
      const datePart = parts[1];
      try {
        const date = new Date(datePart);
        if (!isNaN(date.getTime())) {
          return datePart;
        }
        console.log('Date part is not a valid date:', datePart);
      } catch (error: unknown) {
        console.log('Error parsing date from recording ID:', error);
      }
    }
    return undefined;
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;

      // Format: "Aug 21, 2025, 1:07 PM"
      const dateOptions: Intl.DateTimeFormatOptions = {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      };
      const timeOptions: Intl.DateTimeFormatOptions = {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      };

      const datePart = date.toLocaleDateString('en-US', dateOptions);
      const timePart = date.toLocaleTimeString('en-US', timeOptions);

      return `${datePart}, ${timePart}`;
    } catch {
      return dateString;
    }
  };

  // Get the display date - use recordingDate if provided, otherwise extract from ID
  const displayDate = recordingDate || extractDateFromRecordingId(recordingId);

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch((err) => {
        console.warn('Playback failed:', err);
      });
    }
  };

  const handleSeek = (value: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = value;
    setCurrentTime(value);
  };

  const handleVolumeChange = (value: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = value;
    setVolume(value);
  };

  const handleDownload = async () => {
    if (!recordingId) {
      setError('No recording ID provided');
      return;
    }

    setDownloading(true);
    setError(null);

    try {
      // URL-encode the recording ID to handle special characters like colons
      const encodedRecordingId = encodeURIComponent(recordingId);

      // Construct the full URL with backend URL
      const fullUrl = `${backendUrl}${url}${encodedRecordingId}`;

      // Fetch the recording with authentication from Shesha context
      const response = await axios.get(fullUrl, {
        responseType: 'blob',
        withCredentials: true,
        headers: httpHeaders, // Use Shesha's authenticated headers
      });

      // Get content type from response, default to audio/mpeg.
      // axios types a header as string | number | boolean | string[] | AxiosHeaders, and
      // BlobPropertyBag.type only accepts a string, so anything else falls back to the default.
      const rawContentType = response.headers['content-type'];
      const contentType = typeof rawContentType === 'string' ? rawContentType : 'audio/mpeg';

      // Create a blob from the response
      const blob = new Blob([response.data], { type: contentType });

      // Determine file extension based on content type
      let extension = 'mp3'; // default
      if (contentType.includes('wav')) {
        extension = 'wav';
      } else if (contentType.includes('ogg')) {
        extension = 'ogg';
      } else if (contentType.includes('webm')) {
        extension = 'webm';
      } else if (contentType.includes('m4a')) {
        extension = 'm4a';
      }

      // Create a temporary link element and trigger download
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `recording_${recordingId}.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Cleanup
      URL.revokeObjectURL(downloadUrl);
      setDownloading(false);
    } catch (err: unknown) {
      console.error('Error downloading recording:', err);

      if (isAxiosError(err)) {
        setError(
          err.response?.status === 404
            ? 'Recording not found'
            : err.response?.status === 401
            ? 'Unauthorized: Please log in'
            : 'Failed to download recording. Please try again.'
        );
      } else {
        setError('Failed to download recording. Please try again.');
      }
      setDownloading(false);
    }
  };

  if (!recordingId) {
    return (
      <Card bordered={true} style={cardStyle}>
        <Alert
          message="No Recording ID"
          description={noRecordingText}
          type="info"
          showIcon
        />
      </Card>
    );
  }

  if (loading) {
    return (
      <Card bordered={true} style={cardStyle}>
        <Space direction="vertical" align="center" style={{ width: '100%', padding: '20px 0' }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />} />
          <Text type="secondary">{loadingText}</Text>
        </Space>
      </Card>
    );
  }

  if (error) {
    return (
      <Card bordered={true} style={cardStyle}>
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          action={
            <Button size="small" onClick={loadRecording}>
              Retry
            </Button>
          }
        />
      </Card>
    );
  }

  // Compact mode layout
  if (compact && audioUrl) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', ...cardStyle }}>
        {/* Hidden audio element */}
        <audio
          ref={audioRef}
          preload={preload}
          autoPlay={autoplay}
          loop={loop}
          style={{ display: 'none' }}
        >
          {audioUrl && <source src={audioUrl} />}
        </audio>

        {/* Play/Pause Button */}
        <Tooltip title={isPlaying ? 'Pause' : 'Play'}>
          <Button
            type="text"
            shape="circle"
            size="small"
            icon={isPlaying ? <PauseCircleOutlined style={{ fontSize: 18 }} /> : <PlayCircleOutlined style={{ fontSize: 18 }} />}
            onClick={togglePlayPause}
          />
        </Tooltip>

        {/* Time Display */}
        <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
          {formatTime(currentTime)} / {formatTime(duration)}
        </Text>

        {/* Download Button */}
        {showDownloadButton && (
          <Tooltip title={downloading ? 'Downloading...' : 'Download Recording'}>
            <Button
              type="text"
              shape="circle"
              size="small"
              icon={<DownloadOutlined style={{ fontSize: 16 }} />}
              onClick={handleDownload}
              loading={downloading}
              disabled={!audioUrl}
            />
          </Tooltip>
        )}
      </div>
    );
  }

  return (
    <Card
      className="call-recording-player"
      bordered={true}
      style={cardStyle}
    >
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        preload={preload}
        autoPlay={autoplay}
        loop={loop}
        style={{ display: 'none' }}
      >
        {audioUrl && <source src={audioUrl} />}
      </audio>

      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {/* Header with icon and date (or label if no date) */}
        <Row align="middle" gutter={8} wrap={false}>
          <Col flex="none">
            <CustomerServiceOutlined style={{ fontSize: 24, color: '#1890ff' }} />
          </Col>
          <Col flex="auto">
            {displayDate ? (
              <Text type="secondary" style={{ fontSize: 14 }}>
                {formatDate(displayDate)}
              </Text>
            ) : (
              <Text strong style={{ fontSize: 16 }}>
                {label}
              </Text>
            )}
          </Col>
        </Row>

        {/* Custom Controls */}
        {audioUrl && showControls && (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            {/* Progress Slider */}
            <div>
              <Slider
                min={0}
                max={duration || 100}
                step={0.1}
                value={currentTime}
                onChange={handleSeek}
                tooltip={{ formatter: (value) => formatTime(value || 0) }}
              />
              <Row justify="space-between">
                <Col>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {formatTime(currentTime)}
                  </Text>
                </Col>
                <Col>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {formatTime(duration)}
                  </Text>
                </Col>
              </Row>
            </div>

            {/* Play/Pause and Volume Controls */}
            <Row gutter={16} align="middle" justify="space-between">
              <Col>
                <Space>
                  <Tooltip title={isPlaying ? 'Pause' : 'Play'}>
                    <Button
                      type="primary"
                      shape="circle"
                      size="large"
                      icon={isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                      onClick={togglePlayPause}
                    />
                  </Tooltip>
                </Space>
              </Col>
              <Col flex="auto">
                <Row align="middle" gutter={8}>
                  <Col>
                    <SoundOutlined style={{ fontSize: 16 }} />
                  </Col>
                  <Col flex="auto" style={{ maxWidth: 150 }}>
                    <Slider
                      min={0}
                      max={1}
                      step={0.01}
                      value={volume}
                      onChange={handleVolumeChange}
                      tooltip={{ formatter: (value) => `${Math.round((value || 0) * 100)}%` }}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          </Space>
        )}

        {/* Simple player for when controls are hidden */}
        {audioUrl && !showControls && (
          <Space>
            <Button
              type="primary"
              icon={isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
              onClick={togglePlayPause}
            >
              {isPlaying ? 'Pause' : 'Play'}
            </Button>
            <Text type="secondary">
              {formatTime(currentTime)} / {formatTime(duration)}
            </Text>
          </Space>
        )}

        {/* Download Button */}
        {showDownloadButton && (
          <Button
            type="default"
            icon={<DownloadOutlined />}
            onClick={handleDownload}
            loading={downloading}
            disabled={!audioUrl}
            block
          >
            {downloading ? 'Downloading...' : downloadButtonText}
          </Button>
        )}
      </Space>
    </Card>
  );
};

export default CallRecordingPlayer;
